import { createFileRoute, Link } from "@tanstack/react-router";
import { Rocket, ArrowRight, Play } from "lucide-react";
import sierraHeadshot from "@/assets/sierra-headshot.jpg";
import thankYouVideo from "@/assets/thank-you-video.mp4";
import thankYouPoster from "@/assets/thank-you-poster.jpg";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/thank-you")({
  head: () => ({
    meta: [
      { title: "Thank you — Three Day Launch" },
      { name: "description", content: "Thanks for reaching out! I'll be in touch within 24 business hours." },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Thank you — Three Day Launch" },
      { property: "og:description", content: "Thanks for reaching out! I'll be in touch within 24 business hours." },
    ],
  }),
  component: ThankYouPage,
});

const testimonials = [
  { quote: "OMG I LOVEEEEE my new site!!!", name: "Rachel" },
  { quote: "This site looks boss.", name: "Dave" },
  { quote: "Just booked my first gig from the website!", name: "Beatrice" },
  { quote: "Collaborators are impressed with what we have to offer.", name: "Jenni" },
  { quote: "This site surpasses my wildest dreams.", name: "Beth" },
];

function ThankYouPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
              <Rocket className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back home</Link>
        </div>
      </header>

      <main>
        {/* Hero thank-you */}
        <section className="py-20 md:py-[45px]">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
              Message received
            </div>
            <h1 className="mt-5 text-5xl md:text-7xl font-bold tracking-tight">
              Hey <span className="text-primary">thanks!</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-muted-foreground">
              I so appreciate you reaching out. I'll be in touch within{" "}
              <span className="font-semibold text-foreground">24 business hours</span>.
            </p>
            <p className="mt-6 text-lg md:text-xl">
              But the good news is, you don't have to wait around that long to get the
              <span className="font-semibold"> website of your dreams </span>
              started.
            </p>
          </div>
        </section>

        {/* Video + Sierra */}
        <section className="pb-20">
          <div className="mx-auto max-w-5xl px-6">
            <div className="mb-8 flex items-center justify-center gap-4">
              <div className="h-16 w-16 rounded-full overflow-hidden bg-muted shadow-card-soft shrink-0">
                <img
                  src={sierraHeadshot}
                  alt="Sierra Janisse, Founder & Designer at Three Day Launch"
                  width={256}
                  height={256}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <div className="font-semibold text-lg leading-tight">A word from Sierra</div>
                <div className="text-primary font-semibold text-[11px] uppercase tracking-wider mt-0.5">
                  Founder & Designer
                </div>
              </div>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-card-soft bg-secondary aspect-video max-w-3xl mx-auto">
              <video
                className="w-full h-full object-cover"
                controls
                preload="metadata"
                poster={thankYouPoster}
              >
                <source src={thankYouVideo} type="video/mp4" />
              </video>
            </div>


            <div className="mt-10 text-center">
              <Button
                size="lg"
                className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
              >
                Book my website now <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>

        {/* Testimonials marquee (matches homepage) */}
        <div data-drag-marquee className="bg-secondary text-secondary-foreground py-6 overflow-hidden border-y border-white/5">
          <div className="flex gap-16 marquee whitespace-nowrap text-sm font-semibold tracking-wide text-white/70">
            {[...testimonials, ...testimonials, ...testimonials].map((it, i) => (
              <span key={i} className="flex items-center gap-16">
                <span className="flex flex-col items-start leading-tight">
                  <span className="italic">"{it.quote}"</span>
                  <span className="text-xs text-white/70 normal-case mt-0.5">{it.name}</span>
                </span>
                <span className="h-1.5 w-1.5 rounded-full bg-primary/60" />
              </span>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <section className="py-24">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Don't wait. Your <span className="text-primary">new website</span> is ready when you are.
            </h2>
            <div className="mt-8">
              <Button
                size="lg"
                className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
              >
                Book my website now <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-secondary text-secondary-foreground border-t border-white/10 py-10">
        <div className="mx-auto max-w-7xl px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
              <Rocket className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
          </div>
          <nav className="flex items-center gap-6 text-sm text-white/70">
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
          </nav>
          <div className="text-sm text-white/80">© {new Date().getFullYear()} Three Day Launch. Custom websites for business owners.</div>
        </div>
      </footer>
    </div>
  );
}
